import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/layout/Navbar";
import { Users, Calendar, BookOpen, MessageCircle, Mail, Star, Trash2, Edit, Plus, Shield, Crown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Event form
  const [eventForm, setEventForm] = useState({ title: "", description: "", date: "", location: "", location_type: "in-person", image_url: "", tags: [], is_members_only: false });
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);

  // Blog form
  // Editorial content system — content types & visibility levels
  const CONTENT_TYPE_OPTIONS = [
    { value: "update", label: "Creative Ensemble Update", desc: "News & community updates" },
    { value: "article", label: "Article", desc: "Long-form essay or feature" },
    { value: "how_to", label: "How-To Guide", desc: "Step-by-step instructions" },
    { value: "list", label: "List / Roundup", desc: "Curated lists & roundups" },
    { value: "featured_member", label: "Featured Member", desc: "Spotlight on a member" },
    { value: "featured_business", label: "Featured Business", desc: "Spotlight on a business" },
    { value: "podcast", label: "Podcast Episode", desc: "Audio/video + show notes" },
  ];
  const VISIBILITY_OPTIONS = [
    { value: "public", label: "Public", desc: "Anyone can read on /blog" },
    { value: "portal_only", label: "Members only (Portal)", desc: "Hidden from public blog" },
    { value: "both", label: "Both", desc: "Public + featured in member portal" },
  ];

  const emptyBlogForm = {
    title: "", excerpt: "", content: "", image_url: "", tags: [],
    hide_author: false, hide_date: false, post_as_brand: false, is_published: true,
    // Editorial content system
    content_type: "update",
    visibility: "public",
    gallery_images: [],
    linked_user_id: "",
    linked_business_id: "",
    audio_url: "",
    video_url: "",
    episode_number: "",
    guest_name: "",
    duration: "",
    steps: [],
  };
  const [blogForm, setBlogForm] = useState(emptyBlogForm);
  const [editingPostId, setEditingPostId] = useState(null);
  const [blogTagInput, setBlogTagInput] = useState("");
  const [isBlogDialogOpen, setIsBlogDialogOpen] = useState(false);

  // Testimonial form
  const [testimonialForm, setTestimonialForm] = useState({ name: "", title: "", content: "", company: "" });
  const [isTestimonialDialogOpen, setIsTestimonialDialogOpen] = useState(false);

  // Partners
  const [partners, setPartners] = useState([]);
  const [partnerForm, setPartnerForm] = useState({ name: "", website: "", logo_url: "", display_order: 0 });
  const [isPartnerDialogOpen, setIsPartnerDialogOpen] = useState(false);
  const [partnerLogoUploading, setPartnerLogoUploading] = useState(false);

  // Reminders
  const [incompleteMembers, setIncompleteMembers] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const [selectedReminderIds, setSelectedReminderIds] = useState([]);

  // Contact messages
  const [contactMessages, setContactMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Event pitches (workshop/meetup ideas submitted from the Events page)
  const [pitches, setPitches] = useState([]);
  const [pitchesLoading, setPitchesLoading] = useState(false);

  const loadPitches = async () => {
    setPitchesLoading(true);
    try {
      const res = await axios.get(`${API}/admin/event-pitches`);
      setPitches(res.data);
    } catch { toast.error("Failed to load event pitches"); }
    finally { setPitchesLoading(false); }
  };

  const markPitchRead = async (id) => {
    try {
      await axios.put(`${API}/admin/event-pitches/${id}/read`);
      setPitches((prev) => prev.map((p) => (p.pitch_id === id ? { ...p, is_read: true } : p)));
    } catch { toast.error("Failed to mark as read"); }
  };

  const deletePitch = async (id) => {
    if (!window.confirm("Delete this event pitch?")) return;
    try {
      await axios.delete(`${API}/admin/event-pitches/${id}`);
      setPitches((prev) => prev.filter((p) => p.pitch_id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const loadSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/suggestions`);
      setSuggestions(res.data);
    } catch { toast.error("Failed to load suggestions"); }
    finally { setSuggestionsLoading(false); }
  };

  const markSuggestionRead = async (id) => {
    try {
      await axios.put(`${API}/admin/suggestions/${id}/read`);
      setSuggestions((prev) => prev.map((s) => (s.suggestion_id === id ? { ...s, is_read: true } : s)));
    } catch { toast.error("Failed to mark as read"); }
  };

  const deleteSuggestion = async (id) => {
    if (!window.confirm("Delete this suggestion?")) return;
    try {
      await axios.delete(`${API}/admin/suggestions/${id}`);
      setSuggestions((prev) => prev.filter((s) => s.suggestion_id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const loadContactMessages = async () => {
    setMessagesLoading(true);
    try {
      const res = await axios.get(`${API}/admin/contact-messages`);
      setContactMessages(res.data);
    } catch {
      toast.error("Failed to load messages");
    } finally { setMessagesLoading(false); }
  };

  const markMessageRead = async (id) => {
    try {
      await axios.put(`${API}/admin/contact-messages/${id}/read`);
      setContactMessages((prev) => prev.map((m) => (m.message_id === id ? { ...m, is_read: true } : m)));
    } catch { toast.error("Failed to mark as read"); }
  };

  const deleteContactMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/admin/contact-messages/${id}`);
      setContactMessages((prev) => prev.filter((m) => m.message_id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (user && !user.is_admin) {
      navigate("/dashboard");
      return;
    }
    if (user) {
      Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/members`),
        axios.get(`${API}/events?upcoming=false&limit=50`),
        axios.get(`${API}/blog?limit=50`),
        axios.get(`${API}/discussions?limit=50`),
        axios.get(`${API}/admin/subscribers`),
        axios.get(`${API}/partners`),
        axios.get(`${API}/businesses?limit=500`),
      ]).then(([statsRes, membersRes, eventsRes, postsRes, discussionsRes, subscribersRes, partnersRes, businessesRes]) => {
        setStats(statsRes.data);
        setMembers(membersRes.data);
        setEvents(eventsRes.data);
        setPosts(postsRes.data);
        setDiscussions(discussionsRes.data);
        setSubscribers(subscribersRes.data);
        setPartners(partnersRes.data);
        setAllBusinesses(Array.isArray(businessesRes.data) ? businessesRes.data : (businessesRes.data?.businesses || businessesRes.data?.items || []));
      }).catch((error) => {
        if (error.response?.status === 403) {
          toast.error("Admin access required");
          navigate("/dashboard");
        }
      }).finally(() => setLoading(false));
    }
  }, [user, navigate]);

  const fetchAll = async () => {
    try {
      const [statsRes, membersRes, eventsRes, postsRes, discussionsRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/members`),
        axios.get(`${API}/events?upcoming=false&limit=50`),
        axios.get(`${API}/blog?limit=50`),
        axios.get(`${API}/discussions?limit=50`),
        axios.get(`${API}/admin/subscribers`),
      ]);
      setStats(statsRes.data);
      setMembers(membersRes.data);
      setEvents(eventsRes.data);
      setPosts(postsRes.data);
      setDiscussions(discussionsRes.data);
      setSubscribers(subscribersRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleFoundingMember = async (userId, current) => {
    try {
      await axios.put(`${API}/admin/members/${userId}`, { is_founding_member: !current });
      setMembers(members.map(m => m.user_id === userId ? { ...m, is_founding_member: !current } : m));
      toast.success(`Member ${!current ? "marked as" : "removed from"} founding`);
    } catch (error) {
      toast.error("Failed to update member");
    }
  };

  const changeTier = async (userId, tier) => {
    try {
      await axios.put(`${API}/admin/members/${userId}`, { membership_tier: tier });
      setMembers(members.map(m => m.user_id === userId ? { ...m, membership_tier: tier } : m));
      toast.success("Tier updated");
    } catch (error) {
      toast.error("Failed to update tier");
    }
  };

  const toggleAdmin = async (userId, current) => {
    if (!user?.is_founder) {
      toast.error("Only the Founder can manage admins.");
      return;
    }
    if (!window.confirm(current ? "Remove admin access from this member?" : "Promote this member to Admin?")) return;
    try {
      await axios.post(`${API}/admin/members/${userId}/toggle-admin`, { is_admin: !current });
      setMembers(members.map(m => m.user_id === userId ? { ...m, is_admin: !current } : m));
      toast.success(current ? "Admin removed" : "Admin promoted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const toggleHidden = async (userId, current) => {
    if (!window.confirm(current ? "Unhide this profile? It will reappear in the directory." : "Hide this profile from the directory?")) return;
    try {
      await axios.post(`${API}/admin/members/${userId}/toggle-hidden`, { is_hidden: !current });
      setMembers(members.map(m => m.user_id === userId ? { ...m, is_hidden: !current } : m));
      toast.success(current ? "Profile unhidden" : "Profile hidden from directory");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const deleteMember = async (userId) => {
    if (!window.confirm("Delete this member?")) return;
    try {
      await axios.delete(`${API}/admin/members/${userId}`);
      setMembers(members.filter(m => m.user_id !== userId));
      toast.success("Member deleted");
    } catch (error) {
      toast.error("Failed to delete member");
    }
  };

  // ===== Admin: Edit Member Profile =====
  const [editMember, setEditMember] = useState(null);
  const [editMemberForm, setEditMemberForm] = useState({});
  const [editMemberSaving, setEditMemberSaving] = useState(false);

  const openEditMember = (m) => {
    setEditMember(m);
    setEditMemberForm({
      name: m.name || "",
      email: m.email || "",
      contact_email: m.contact_email || "",
      title: m.title || "",
      company: m.company || "",
      bio: m.bio || "",
      location: m.location || "",
      industry: m.industry || "",
      website: m.website || "",
      instagram: m.instagram || "",
      linkedin: m.linkedin || "",
      skills: (m.skills || []).join(", "),
      goals: (m.goals || []).join(", "),
    });
  };

  const saveEditMember = async (e) => {
    e.preventDefault();
    if (!editMember) return;
    setEditMemberSaving(true);
    try {
      const payload = {
        ...editMemberForm,
        skills: editMemberForm.skills
          ? editMemberForm.skills.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        goals: editMemberForm.goals
          ? editMemberForm.goals.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      };
      await axios.put(`${API}/admin/members/${editMember.user_id}`, payload);
      toast.success("Member profile updated");
      setEditMember(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setEditMemberSaving(false);
    }
  };

  // ===== Admin: Reset Member Password =====
  const [resetPwMember, setResetPwMember] = useState(null);
  const [resetPwInput, setResetPwInput] = useState("");
  const [resetPwResult, setResetPwResult] = useState(null);
  const [resetPwSaving, setResetPwSaving] = useState(false);

  const openResetPw = (m) => {
    setResetPwMember(m);
    setResetPwInput("");
    setResetPwResult(null);
  };

  const submitResetPw = async (e) => {
    e.preventDefault();
    if (!resetPwMember) return;
    setResetPwSaving(true);
    try {
      const res = await axios.post(`${API}/admin/members/${resetPwMember.user_id}/reset-password`, {
        new_password: resetPwInput || undefined,
      });
      setResetPwResult(res.data);
      toast.success("Password reset");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetPwSaving(false);
    }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events`, { ...eventForm, date: new Date(eventForm.date).toISOString() });
      toast.success("Event created!");
      setIsEventDialogOpen(false);
      setEventForm({ title: "", description: "", date: "", location: "", location_type: "in-person", tags: [], is_members_only: false });
      fetchAll();
    } catch (error) {
      toast.error("Failed to create event");
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/admin/events/${eventId}`);
      setEvents(events.filter(e => e.event_id !== eventId));
      toast.success("Event deleted");
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const openCreateBlog = () => {
    setEditingPostId(null);
    setBlogForm(emptyBlogForm);
    setBlogTagInput("");
    setIsBlogDialogOpen(true);
  };

  const openEditBlog = (post) => {
    setEditingPostId(post.post_id);
    setBlogForm({
      title: post.title || "",
      excerpt: post.excerpt || "",
      content: post.content || "",
      image_url: post.image_url || "",
      tags: post.tags || [],
      hide_author: !!post.hide_author,
      hide_date: !!post.hide_date,
      post_as_brand: !!post.post_as_brand,
      is_published: post.is_published !== false,
      content_type: post.content_type || "update",
      visibility: post.visibility || "public",
      gallery_images: post.gallery_images || [],
      linked_user_id: post.linked_user_id || "",
      linked_business_id: post.linked_business_id || "",
      audio_url: post.audio_url || "",
      video_url: post.video_url || "",
      episode_number: post.episode_number || "",
      guest_name: post.guest_name || "",
      duration: post.duration || "",
      steps: post.steps || [],
    });
    setBlogTagInput("");
    setIsBlogDialogOpen(true);
  };

  const submitBlogPost = async (e) => {
    e.preventDefault();
    try {
      // Normalize: empty strings → null for optional fields the backend expects as nullable
      const payload = { ...blogForm };
      if (payload.episode_number === "" || payload.episode_number === null) payload.episode_number = null;
      ["audio_url", "video_url", "guest_name", "duration", "linked_user_id", "linked_business_id"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (editingPostId) {
        await axios.put(`${API}/admin/blog/${editingPostId}`, payload);
        toast.success("Blog post updated!");
      } else {
        await axios.post(`${API}/blog`, payload);
        toast.success("Blog post created!");
      }
      setIsBlogDialogOpen(false);
      setEditingPostId(null);
      setBlogForm(emptyBlogForm);
      fetchAll();
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(msg || (editingPostId ? "Failed to update post" : "Failed to create post"));
    }
  };

  const addBlogTag = () => {
    const t = blogTagInput.trim();
    if (!t || blogForm.tags.includes(t)) { setBlogTagInput(""); return; }
    setBlogForm({ ...blogForm, tags: [...blogForm.tags, t] });
    setBlogTagInput("");
  };

  const removeBlogTag = (t) => {
    setBlogForm({ ...blogForm, tags: blogForm.tags.filter(x => x !== t) });
  };

  const deleteBlogPost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/admin/blog/${postId}`);
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success("Post deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const deleteDiscussion = async (discussionId) => {
    if (!window.confirm("Delete this discussion?")) return;
    try {
      await axios.delete(`${API}/admin/discussions/${discussionId}`);
      setDiscussions(discussions.filter(d => d.discussion_id !== discussionId));
      toast.success("Discussion deleted");
    } catch (error) {
      toast.error("Failed to delete discussion");
    }
  };

  const createTestimonial = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/testimonials`, testimonialForm);
      toast.success("Testimonial created!");
      setIsTestimonialDialogOpen(false);
      setTestimonialForm({ name: "", title: "", content: "", company: "" });
    } catch (error) {
      toast.error("Failed to create testimonial");
    }
  };

  const handlePartnerLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPartnerLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload/partner-logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPartnerForm((prev) => ({ ...prev, logo_url: res.data.url }));
      toast.success("Logo uploaded");
    } catch {
      toast.error("Failed to upload logo");
    } finally {
      setPartnerLogoUploading(false);
    }
  };

  const createPartner = async (e) => {
    e.preventDefault();
    if (!partnerForm.logo_url) { toast.error("Please upload a logo"); return; }
    try {
      const res = await axios.post(`${API}/partners`, partnerForm);
      setPartners([...partners, res.data].sort((a, b) => (a.display_order || 0) - (b.display_order || 0)));
      toast.success("Partner added");
      setIsPartnerDialogOpen(false);
      setPartnerForm({ name: "", website: "", logo_url: "", display_order: 0 });
    } catch {
      toast.error("Failed to add partner");
    }
  };

  const deletePartner = async (partnerId) => {
    if (!window.confirm("Remove this partner?")) return;
    try {
      await axios.delete(`${API}/partners/${partnerId}`);
      setPartners(partners.filter((p) => p.partner_id !== partnerId));
      toast.success("Partner removed");
    } catch {
      toast.error("Failed to remove partner");
    }
  };

  const loadIncomplete = async () => {
    setRemindersLoading(true);
    try {
      const res = await axios.get(`${API}/admin/incomplete-profiles`);
      setIncompleteMembers(res.data);
      setSelectedReminderIds(res.data.map((m) => m.user_id)); // default: all selected
    } catch {
      toast.error("Failed to load incomplete profiles");
    } finally { setRemindersLoading(false); }
  };

  const toggleReminderSelection = (userId) => {
    setSelectedReminderIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAllReminders = () => {
    if (selectedReminderIds.length === incompleteMembers.length) {
      setSelectedReminderIds([]);
    } else {
      setSelectedReminderIds(incompleteMembers.map((m) => m.user_id));
    }
  };

  const sendReminders = async (tone) => {
    if (selectedReminderIds.length === 0) {
      toast.error("Select at least one member");
      return;
    }
    const label = tone === "firm" ? "FINAL DEADLINE" : "friendly nudge";
    if (!window.confirm(`Send ${label} email to ${selectedReminderIds.length} selected member(s)?`)) return;
    setReminderSending(true);
    try {
      const res = await axios.post(`${API}/admin/send-profile-reminders`, { tone, user_ids: selectedReminderIds });
      const { sent_count, failed_count } = res.data;
      if (failed_count > 0) {
        toast.warning(`Sent ${sent_count} ✓ — ${failed_count} failed. Check console for details.`);
        console.warn("Reminder failures:", res.data.failed);
      } else {
        toast.success(`Sent ${sent_count} reminder${sent_count === 1 ? "" : "s"}`);
      }
      loadIncomplete();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to send reminders";
      toast.error(msg);
    } finally { setReminderSending(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <div className="container-main py-12 text-center">
          <div className="spinner mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-panel">
      <Navbar />

      <div className="container-main py-12">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-[#FF2D87]" />
          <h1 className="text-3xl font-display font-bold">Admin Panel</h1>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {[
              { label: "Members", value: stats.total_users, icon: Users, color: "bg-[#5170FF]" },
              { label: "Founding", value: stats.founding_members, icon: Crown, color: "bg-[#FF2D87]" },
              { label: "Events", value: stats.total_events, icon: Calendar, color: "bg-[#FFC2E8]" },
              { label: "Posts", value: stats.total_posts, icon: BookOpen, color: "bg-[#0A0A0A]" },
              { label: "Discussions", value: stats.total_discussions, icon: MessageCircle, color: "bg-[#5170FF]" },
              { label: "Subscribers", value: stats.total_subscribers, icon: Mail, color: "bg-[#FF2D87]" },
            ].map((stat) => (
              <div key={stat.label} className="bg-white border-2 border-[#0A0A0A] shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] p-4" data-testid={`stat-${stat.label.toLowerCase()}`}>
                <div className={`w-10 h-10 ${stat.color} flex items-center justify-center mb-3`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-xs text-[#4A4A4A] uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs
          defaultValue="members"
          onValueChange={(val) => {
            if (val === "messages" && contactMessages.length === 0) loadContactMessages();
            if (val === "suggestions" && suggestions.length === 0) loadSuggestions();
            if (val === "pitches" && pitches.length === 0) loadPitches();
          }}
        >
          <TabsList className="mb-8 bg-white border-2 border-[#0A0A0A] rounded-none p-1 flex flex-wrap h-auto">
            <TabsTrigger value="members" className="tab-trigger rounded-none data-[state=active]:bg-[#FFC2E8]">Members</TabsTrigger>
            <TabsTrigger value="events" className="tab-trigger rounded-none data-[state=active]:bg-[#FFC2E8]">Events</TabsTrigger>
            <TabsTrigger value="blog" className="tab-trigger rounded-none data-[state=active]:bg-[#FFC2E8]">Blog</TabsTrigger>
            <TabsTrigger value="discussions" className="tab-trigger rounded-none data-[state=active]:bg-[#FFC2E8]">Discussions</TabsTrigger>
            <TabsTrigger value="subscribers" className="tab-trigger rounded-none data-[state=active]:bg-[#FFC2E8]">Subscribers</TabsTrigger>
            <TabsTrigger value="testimonials" className="tab-trigger rounded-none data-[state=active]:bg-[#FFC2E8]">Testimonials</TabsTrigger>
            <TabsTrigger value="partners" className="tab-trigger rounded-none data-[state=active]:bg-[#FFC2E8]">Partners</TabsTrigger>
            <TabsTrigger value="reminders" className="tab-trigger rounded-none data-[state=active]:bg-[#FFC2E8]">Reminders</TabsTrigger>
            <TabsTrigger value="messages" className="tab-trigger rounded-none data-[state=active]:bg-[#FFC2E8]" data-testid="tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="suggestions" className="tab-trigger rounded-none data-[state=active]:bg-[#FFC2E8]" data-testid="tab-suggestions">Suggestions</TabsTrigger>
            <TabsTrigger value="pitches" className="tab-trigger rounded-none data-[state=active]:bg-[#FFC2E8]" data-testid="tab-pitches">Event Pitches</TabsTrigger>
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members">
            <div className="space-y-3">
              {members.map((member) => (
                <div key={member.user_id} className="bg-white border-2 border-[#E5E5E5] p-4 flex flex-wrap items-center gap-4" data-testid={`admin-member-${member.user_id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                    <div className="w-10 h-10 rounded-full bg-[#5170FF] flex items-center justify-center text-white font-bold text-sm">
                      {member.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-bold text-sm">{member.name}</p>
                      <p className="text-xs text-[#4A4A4A]">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {member.is_founding_member && <span className="badge badge-secondary text-[10px]">Founding</span>}
                    {member.is_admin && <span className="badge badge-primary text-[10px]">Admin</span>}
                    <select
                      value={member.membership_tier || "free"}
                      onChange={(e) => changeTier(member.user_id, e.target.value)}
                      className="text-xs border border-[#E5E5E5] px-2 py-1"
                    >
                      <option value="free">Free</option>
                      <option value="member">Member</option>
                      <option value="collective">Collective</option>
                    </select>
                    <button
                      onClick={() => toggleFoundingMember(member.user_id, member.is_founding_member)}
                      className={`text-xs px-3 py-1 border-2 font-bold ${member.is_founding_member ? "border-[#FF2D87] text-[#FF2D87]" : "border-[#E5E5E5] text-[#4A4A4A]"}`}
                      data-testid={`toggle-founding-${member.user_id}`}
                    >
                      <Crown className="w-3 h-3 inline mr-1" />
                      {member.is_founding_member ? "Founding" : "Set Founding"}
                    </button>
                    {user?.is_founder && (
                      <button
                        onClick={() => toggleAdmin(member.user_id, member.is_admin)}
                        className={`text-xs px-3 py-1 border-2 font-bold ${member.is_admin ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"} transition-colors`}
                        data-testid={`toggle-admin-${member.user_id}`}
                      >
                        {member.is_admin ? "Remove Admin" : "Make Admin"}
                      </button>
                    )}
                    <button
                      onClick={() => toggleHidden(member.user_id, member.is_hidden)}
                      className={`text-xs px-3 py-1 border-2 font-bold transition-colors ${member.is_hidden ? "bg-[#FF2D87] text-white border-[#FF2D87]" : "border-[#FF2D87] text-[#FF2D87] hover:bg-[#FF2D87] hover:text-white"}`}
                      data-testid={`toggle-hidden-${member.user_id}`}
                    >
                      {member.is_hidden ? "Hidden" : "Hide"}
                    </button>
                    <button
                      onClick={() => openEditMember(member)}
                      className="text-xs px-3 py-1 border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white font-bold transition-colors"
                      data-testid={`edit-member-${member.user_id}`}
                      title="Edit profile"
                    >
                      <Edit className="w-3 h-3 inline mr-1" /> Edit
                    </button>
                    <button
                      onClick={() => openResetPw(member)}
                      className="text-xs px-3 py-1 border-2 border-[#5170FF] text-[#5170FF] hover:bg-[#5170FF] hover:text-white font-bold transition-colors"
                      data-testid={`reset-pw-${member.user_id}`}
                      title="Reset password"
                    >
                      Reset PW
                    </button>
                    <button onClick={() => deleteMember(member.user_id)} className="text-[#FF2D87] hover:text-[#0A0A0A]">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {members.length === 0 && <p className="text-center text-[#4A4A4A] py-12">No members yet</p>}
            </div>

            {/* Edit Member Profile Dialog */}
            <Dialog open={!!editMember} onOpenChange={(open) => !open && setEditMember(null)}>
              <DialogContent className="dialog-content sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-display">
                    Edit profile — {editMember?.name || editMember?.email}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={saveEditMember} className="space-y-4 mt-4" data-testid="edit-member-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Name</label>
                      <input type="text" value={editMemberForm.name || ""} onChange={(e) => setEditMemberForm({...editMemberForm, name: e.target.value})} className="input-underline" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Login email</label>
                      <input type="email" value={editMemberForm.email || ""} onChange={(e) => setEditMemberForm({...editMemberForm, email: e.target.value})} className="input-underline" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Contact email (public)</label>
                      <input type="email" value={editMemberForm.contact_email || ""} onChange={(e) => setEditMemberForm({...editMemberForm, contact_email: e.target.value})} className="input-underline" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Title</label>
                      <input type="text" value={editMemberForm.title || ""} onChange={(e) => setEditMemberForm({...editMemberForm, title: e.target.value})} className="input-underline" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Company</label>
                      <input type="text" value={editMemberForm.company || ""} onChange={(e) => setEditMemberForm({...editMemberForm, company: e.target.value})} className="input-underline" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Location</label>
                      <input type="text" value={editMemberForm.location || ""} onChange={(e) => setEditMemberForm({...editMemberForm, location: e.target.value})} className="input-underline" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Industry</label>
                      <input type="text" value={editMemberForm.industry || ""} onChange={(e) => setEditMemberForm({...editMemberForm, industry: e.target.value})} className="input-underline" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Bio</label>
                    <textarea rows={4} value={editMemberForm.bio || ""} onChange={(e) => setEditMemberForm({...editMemberForm, bio: e.target.value})} className="form-textarea" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Skills (comma-separated)</label>
                    <input type="text" value={editMemberForm.skills || ""} onChange={(e) => setEditMemberForm({...editMemberForm, skills: e.target.value})} className="input-underline" placeholder="Brand Strategy, Content Creation, ..." />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Goals (comma-separated)</label>
                    <input type="text" value={editMemberForm.goals || ""} onChange={(e) => setEditMemberForm({...editMemberForm, goals: e.target.value})} className="input-underline" placeholder="Find collaborators, ..." />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Website</label>
                      <input type="text" value={editMemberForm.website || ""} onChange={(e) => setEditMemberForm({...editMemberForm, website: e.target.value})} className="input-underline" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Instagram</label>
                      <input type="text" value={editMemberForm.instagram || ""} onChange={(e) => setEditMemberForm({...editMemberForm, instagram: e.target.value})} className="input-underline" placeholder="@handle" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">LinkedIn URL</label>
                      <input type="text" value={editMemberForm.linkedin || ""} onChange={(e) => setEditMemberForm({...editMemberForm, linkedin: e.target.value})} className="input-underline" />
                    </div>
                  </div>
                  <button type="submit" disabled={editMemberSaving} className="btn-primary w-full" data-testid="save-member-edit-btn">
                    {editMemberSaving ? "Saving…" : "Save changes"}
                  </button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={!!resetPwMember} onOpenChange={(open) => { if (!open) { setResetPwMember(null); setResetPwResult(null); }}}>
              <DialogContent className="dialog-content sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-display">
                    Reset password — {resetPwMember?.name || resetPwMember?.email}
                  </DialogTitle>
                </DialogHeader>
                {resetPwResult ? (
                  <div className="space-y-4 mt-4">
                    <p className="text-sm text-[#4A4A4A]">
                      Password reset successfully. Share these credentials with the member privately —
                      they should change their password after logging in.
                    </p>
                    <div className="bg-[#FFC2E8] border-2 border-[#0A0A0A] p-4 space-y-2 font-mono text-sm">
                      <div><span className="text-[#0A0A0A]/60 text-xs uppercase tracking-wider">Email:</span> <span className="font-bold break-all">{resetPwResult.email}</span></div>
                      <div><span className="text-[#0A0A0A]/60 text-xs uppercase tracking-wider">New password:</span> <span className="font-bold break-all" data-testid="reset-pw-display">{resetPwResult.new_password}</span></div>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`Email: ${resetPwResult.email}\nPassword: ${resetPwResult.new_password}`);
                        toast.success("Copied to clipboard");
                      }}
                      className="btn-secondary w-full"
                      data-testid="copy-reset-pw-btn"
                    >
                      Copy email + password
                    </button>
                    <button
                      onClick={() => { setResetPwMember(null); setResetPwResult(null); }}
                      className="btn-primary w-full"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submitResetPw} className="space-y-4 mt-4">
                    <p className="text-sm text-[#4A4A4A]">
                      Set a new password for this member, or leave blank to auto-generate a strong one.
                      Their existing sessions will be logged out.
                    </p>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">New password (optional, min 8 chars)</label>
                      <input
                        type="text"
                        value={resetPwInput}
                        onChange={(e) => setResetPwInput(e.target.value)}
                        placeholder="Leave blank to auto-generate"
                        className="input-underline"
                        data-testid="reset-pw-input"
                      />
                    </div>
                    <button type="submit" disabled={resetPwSaving} className="btn-primary w-full" data-testid="submit-reset-pw-btn">
                      {resetPwSaving ? "Resetting…" : "Reset password"}
                    </button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="mb-6">
              <Dialog open={isEventDialogOpen} onOpenChange={setIsEventDialogOpen}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="add-event-btn">
                    <Plus className="w-4 h-4" /> Add Event
                  </button>
                </DialogTrigger>
                <DialogContent className="dialog-content sm:max-w-lg">
                  <DialogHeader><DialogTitle className="text-2xl font-display">Add Event</DialogTitle></DialogHeader>
                  <form onSubmit={createEvent} className="space-y-4 mt-4">
                    <input type="text" value={eventForm.title} onChange={(e) => setEventForm({...eventForm, title: e.target.value})} placeholder="Event title" className="input-underline" required />
                    <textarea value={eventForm.description} onChange={(e) => setEventForm({...eventForm, description: e.target.value})} placeholder="Description" className="form-textarea" rows={3} required />
                    <input type="datetime-local" value={eventForm.date} onChange={(e) => setEventForm({...eventForm, date: e.target.value})} className="input-underline" required />
                    <input type="text" value={eventForm.location} onChange={(e) => setEventForm({...eventForm, location: e.target.value})} placeholder="Location" className="input-underline" required />
                    <select value={eventForm.location_type} onChange={(e) => setEventForm({...eventForm, location_type: e.target.value})} className="w-full px-4 py-3 border-2 border-[#0A0A0A] bg-white">
                      <option value="in-person">In-Person</option>
                      <option value="virtual">Virtual</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Featured image</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          accept="image/*"
                          data-testid="event-image-input"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const fd = new FormData();
                              fd.append("file", file);
                              const res = await axios.post(`${API}/upload/event-image`, fd, { headers: { "Content-Type": "multipart/form-data" }});
                              setEventForm((prev) => ({ ...prev, image_url: res.data.url }));
                              toast.success("Image uploaded");
                            } catch {
                              toast.error("Upload failed");
                            }
                          }}
                          className="text-sm"
                        />
                        {eventForm.image_url && (
                          <button type="button" onClick={() => setEventForm({...eventForm, image_url: ""})} className="text-xs underline text-[#FF2D87]">Remove</button>
                        )}
                      </div>
                      {eventForm.image_url && (
                        <img src={eventForm.image_url} alt="Featured" className="mt-2 w-40 h-24 object-cover border-2 border-[#0A0A0A]" />
                      )}
                    </div>
                    <button type="submit" className="btn-primary w-full">Create Event</button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-3">
              {events.map((event) => (
                <div key={event.event_id} className="bg-white border-2 border-[#E5E5E5] p-4 flex justify-between items-center">
                  <div>
                    <p className="font-bold">{event.title}</p>
                    <p className="text-sm text-[#4A4A4A]">{new Date(event.date).toLocaleDateString()} — {event.location}</p>
                  </div>
                  <button onClick={() => deleteEvent(event.event_id)} className="text-[#FF2D87] hover:text-[#0A0A0A]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {events.length === 0 && <p className="text-center text-[#4A4A4A] py-12">No events yet</p>}
            </div>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <div className="mb-6">
              <button onClick={openCreateBlog} className="btn-primary flex items-center gap-2" data-testid="add-blog-btn">
                <Plus className="w-4 h-4" /> Add Blog Post
              </button>
              <Dialog open={isBlogDialogOpen} onOpenChange={setIsBlogDialogOpen}>
                <DialogContent className="dialog-content sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-display">
                      {editingPostId ? "Edit Blog Post" : "Add Blog Post"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submitBlogPost} className="space-y-5 mt-4">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Title</label>
                      <input type="text" value={blogForm.title} onChange={(e) => setBlogForm({...blogForm, title: e.target.value})} placeholder="Post title" className="input-underline" data-testid="blog-title-input" required />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Excerpt</label>
                      <input type="text" value={blogForm.excerpt} onChange={(e) => setBlogForm({...blogForm, excerpt: e.target.value})} placeholder="Short excerpt (1–2 sentences)" className="input-underline" data-testid="blog-excerpt-input" required />
                    </div>

                    {/* CONTENT TYPE + VISIBILITY — pick first, dynamic fields follow */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-[#FFC2E8]/30 border-2 border-[#FF2D87] rounded-lg">
                      <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A] mb-1 block">Content type</label>
                        <select
                          value={blogForm.content_type}
                          onChange={(e) => setBlogForm({ ...blogForm, content_type: e.target.value })}
                          className="w-full bg-white border-2 border-[#0A0A0A] rounded-none px-3 py-2 text-sm font-bold"
                          data-testid="blog-content-type-select"
                        >
                          {CONTENT_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <p className="text-[10px] text-[#4A4A4A] mt-1">
                          {CONTENT_TYPE_OPTIONS.find((o) => o.value === blogForm.content_type)?.desc}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A] mb-1 block">Visibility</label>
                        <select
                          value={blogForm.visibility}
                          onChange={(e) => setBlogForm({ ...blogForm, visibility: e.target.value })}
                          className="w-full bg-white border-2 border-[#0A0A0A] rounded-none px-3 py-2 text-sm font-bold"
                          data-testid="blog-visibility-select"
                        >
                          {VISIBILITY_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <p className="text-[10px] text-[#4A4A4A] mt-1">
                          {VISIBILITY_OPTIONS.find((o) => o.value === blogForm.visibility)?.desc}
                        </p>
                      </div>
                    </div>

                    {/* FEATURED MEMBER — link to existing member */}
                    {blogForm.content_type === "featured_member" && (
                      <div className="p-4 bg-[#5170FF]/10 border-2 border-[#5170FF] rounded-lg" data-testid="blog-featured-member-section">
                        <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A] mb-1 block">Featured member</label>
                        <select
                          value={blogForm.linked_user_id}
                          onChange={(e) => setBlogForm({ ...blogForm, linked_user_id: e.target.value })}
                          className="w-full bg-white border-2 border-[#0A0A0A] rounded-none px-3 py-2 text-sm"
                          data-testid="blog-linked-user-select"
                        >
                          <option value="">— Choose a member —</option>
                          {members.map((m) => (
                            <option key={m.user_id} value={m.user_id}>
                              {m.name} {m.email ? `(${m.email})` : ""}
                            </option>
                          ))}
                        </select>
                        <p className="text-[10px] text-[#4A4A4A] mt-1">Their bio, photo & social links will be auto-displayed on the post.</p>
                      </div>
                    )}

                    {/* FEATURED BUSINESS — link to existing business */}
                    {blogForm.content_type === "featured_business" && (
                      <div className="p-4 bg-[#5170FF]/10 border-2 border-[#5170FF] rounded-lg" data-testid="blog-featured-business-section">
                        <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A] mb-1 block">Featured business</label>
                        <select
                          value={blogForm.linked_business_id}
                          onChange={(e) => setBlogForm({ ...blogForm, linked_business_id: e.target.value })}
                          className="w-full bg-white border-2 border-[#0A0A0A] rounded-none px-3 py-2 text-sm"
                          data-testid="blog-linked-business-select"
                        >
                          <option value="">— Choose a business —</option>
                          {allBusinesses.map((b) => (
                            <option key={b.business_id} value={b.business_id}>
                              {b.name} {b.industry ? `· ${b.industry}` : ""}
                            </option>
                          ))}
                        </select>
                        <p className="text-[10px] text-[#4A4A4A] mt-1">Logo, website & socials will be auto-displayed on the post.</p>
                      </div>
                    )}

                    {/* PODCAST — episode metadata */}
                    {blogForm.content_type === "podcast" && (
                      <div className="p-4 bg-[#FFC2E8]/40 border-2 border-[#FF2D87] rounded-lg space-y-3" data-testid="blog-podcast-section">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Podcast details</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Episode #</label>
                            <input type="number" value={blogForm.episode_number}
                              onChange={(e) => setBlogForm({ ...blogForm, episode_number: e.target.value ? parseInt(e.target.value) : "" })}
                              className="input-underline" data-testid="blog-podcast-episode" />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Duration (e.g. 42:18)</label>
                            <input type="text" value={blogForm.duration}
                              onChange={(e) => setBlogForm({ ...blogForm, duration: e.target.value })}
                              className="input-underline" data-testid="blog-podcast-duration" />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Guest name(s)</label>
                          <input type="text" value={blogForm.guest_name}
                            onChange={(e) => setBlogForm({ ...blogForm, guest_name: e.target.value })}
                            placeholder="Jane Doe, Co-Founder @ XYZ"
                            className="input-underline" data-testid="blog-podcast-guest" />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Audio URL (mp3, Spotify embed, etc.)</label>
                          <input type="url" value={blogForm.audio_url}
                            onChange={(e) => setBlogForm({ ...blogForm, audio_url: e.target.value })}
                            placeholder="https://..."
                            className="input-underline" data-testid="blog-podcast-audio" />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Video URL (YouTube, Vimeo)</label>
                          <input type="url" value={blogForm.video_url}
                            onChange={(e) => setBlogForm({ ...blogForm, video_url: e.target.value })}
                            placeholder="https://youtube.com/watch?v=..."
                            className="input-underline" data-testid="blog-podcast-video" />
                        </div>
                      </div>
                    )}

                    {/* HOW-TO — step-by-step builder */}
                    {blogForm.content_type === "how_to" && (
                      <div className="p-4 bg-[#FFC2E8]/40 border-2 border-[#FF2D87] rounded-lg space-y-3" data-testid="blog-howto-section">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Steps</p>
                          <button type="button"
                            onClick={() => setBlogForm({ ...blogForm, steps: [...blogForm.steps, { title: "", content: "", image_url: "" }] })}
                            className="text-xs font-bold uppercase tracking-wider text-[#FF2D87] hover:text-[#0A0A0A]"
                            data-testid="blog-howto-add-step">
                            + Add step
                          </button>
                        </div>
                        {blogForm.steps.length === 0 && (
                          <p className="text-xs text-[#4A4A4A] italic">No steps yet. Click "+ Add step" to begin.</p>
                        )}
                        {blogForm.steps.map((step, idx) => (
                          <div key={idx} className="bg-white border-2 border-[#0A0A0A] p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold uppercase tracking-wider">Step {idx + 1}</span>
                              <div className="flex gap-1">
                                <button type="button" disabled={idx === 0}
                                  onClick={() => {
                                    const next = [...blogForm.steps];
                                    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                                    setBlogForm({ ...blogForm, steps: next });
                                  }}
                                  className="text-xs px-2 py-0.5 border border-[#0A0A0A] disabled:opacity-30">↑</button>
                                <button type="button" disabled={idx === blogForm.steps.length - 1}
                                  onClick={() => {
                                    const next = [...blogForm.steps];
                                    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                                    setBlogForm({ ...blogForm, steps: next });
                                  }}
                                  className="text-xs px-2 py-0.5 border border-[#0A0A0A] disabled:opacity-30">↓</button>
                                <button type="button"
                                  onClick={() => setBlogForm({ ...blogForm, steps: blogForm.steps.filter((_, i) => i !== idx) })}
                                  className="text-xs px-2 py-0.5 border border-[#FF2D87] text-[#FF2D87]">Remove</button>
                              </div>
                            </div>
                            <input type="text" value={step.title}
                              onChange={(e) => {
                                const next = [...blogForm.steps];
                                next[idx] = { ...next[idx], title: e.target.value };
                                setBlogForm({ ...blogForm, steps: next });
                              }}
                              placeholder="Step title"
                              className="input-underline" />
                            <textarea value={step.content}
                              onChange={(e) => {
                                const next = [...blogForm.steps];
                                next[idx] = { ...next[idx], content: e.target.value };
                                setBlogForm({ ...blogForm, steps: next });
                              }}
                              placeholder="Step instructions…"
                              rows={3}
                              className="w-full border-2 border-[#0A0A0A] rounded-none px-3 py-2 text-sm" />
                            <div className="flex items-center gap-2">
                              <input type="file" accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  try {
                                    const fd = new FormData(); fd.append("file", file);
                                    const res = await axios.post(`${API}/upload/blog-image`, fd, { headers: { "Content-Type": "multipart/form-data" }});
                                    const next = [...blogForm.steps];
                                    next[idx] = { ...next[idx], image_url: res.data.url };
                                    setBlogForm({ ...blogForm, steps: next });
                                    toast.success("Step image uploaded");
                                  } catch { toast.error("Upload failed"); }
                                }}
                                className="text-xs" />
                              {step.image_url && (
                                <>
                                  <img src={step.image_url} alt={`Step ${idx + 1}`} className="w-16 h-12 object-cover border border-[#0A0A0A]" />
                                  <button type="button" className="text-xs underline text-[#FF2D87]"
                                    onClick={() => {
                                      const next = [...blogForm.steps];
                                      next[idx] = { ...next[idx], image_url: "" };
                                      setBlogForm({ ...blogForm, steps: next });
                                    }}>Remove</button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Featured image</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          accept="image/*"
                          data-testid="blog-image-input"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const fd = new FormData();
                              fd.append("file", file);
                              const res = await axios.post(`${API}/upload/blog-image`, fd, { headers: { "Content-Type": "multipart/form-data" }});
                              setBlogForm((prev) => ({ ...prev, image_url: res.data.url }));
                              toast.success("Image uploaded");
                            } catch {
                              toast.error("Upload failed");
                            }
                          }}
                          className="text-sm"
                        />
                        {blogForm.image_url && (
                          <button type="button" onClick={() => setBlogForm({...blogForm, image_url: ""})} className="text-xs underline text-[#FF2D87]">Remove</button>
                        )}
                      </div>
                      {blogForm.image_url && (
                        <img src={blogForm.image_url} alt="Featured" className="mt-2 w-40 h-24 object-cover border-2 border-[#0A0A0A]" />
                      )}
                    </div>

                    {/* GALLERY — additional images (any content type) */}
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">
                        Gallery images <span className="text-[#4A4A4A] normal-case font-normal">(optional — extra photos beyond the featured image)</span>
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        data-testid="blog-gallery-input"
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          if (!files.length) return;
                          const uploaded = [];
                          for (const file of files) {
                            try {
                              const fd = new FormData();
                              fd.append("file", file);
                              const res = await axios.post(`${API}/upload/blog-image`, fd, { headers: { "Content-Type": "multipart/form-data" }});
                              uploaded.push(res.data.url);
                            } catch {
                              toast.error(`Upload failed for ${file.name}`);
                            }
                          }
                          if (uploaded.length) {
                            setBlogForm((prev) => ({ ...prev, gallery_images: [...(prev.gallery_images || []), ...uploaded] }));
                            toast.success(`${uploaded.length} image(s) added to gallery`);
                          }
                          e.target.value = "";
                        }}
                        className="text-sm"
                      />
                      {blogForm.gallery_images && blogForm.gallery_images.length > 0 && (
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-2">
                          {blogForm.gallery_images.map((url, idx) => (
                            <div key={`${url}-${idx}`} className="relative group">
                              <img src={url} alt={`Gallery ${idx + 1}`} className="w-full h-20 object-cover border-2 border-[#0A0A0A]" />
                              <button
                                type="button"
                                onClick={() => setBlogForm((prev) => ({ ...prev, gallery_images: prev.gallery_images.filter((_, i) => i !== idx) }))}
                                className="absolute top-1 right-1 bg-[#FF2D87] text-white text-xs w-5 h-5 flex items-center justify-center rounded-full hover:bg-[#0A0A0A]"
                                title="Remove">×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Content</label>
                      <RichTextEditor
                        value={blogForm.content}
                        onChange={(html) => setBlogForm((prev) => ({ ...prev, content: html }))}
                        placeholder="Write your post — use the toolbar to format text."
                        testId="blog-content-editor"
                        minHeight={300}
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70 mb-1 block">Tags</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={blogTagInput}
                          onChange={(e) => setBlogTagInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBlogTag(); }}}
                          placeholder="Type a tag and press Enter"
                          className="input-underline flex-1"
                          data-testid="blog-tag-input"
                        />
                        <button type="button" onClick={addBlogTag} className="btn-secondary text-xs px-4">Add</button>
                      </div>
                      {blogForm.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {blogForm.tags.map((t) => (
                            <span key={t} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border border-[#0A0A0A] bg-[#FFC2E8]">
                              {t}
                              <button type="button" onClick={() => removeBlogTag(t)} className="text-[#0A0A0A] hover:text-[#FF2D87]">×</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t-2 border-[#E5E5E5]">
                      <label className="flex items-center gap-2 text-sm" data-testid="blog-hide-author-toggle">
                        <input type="checkbox" checked={blogForm.hide_author} onChange={(e) => setBlogForm({...blogForm, hide_author: e.target.checked})} className="w-4 h-4 accent-[#FF2D87]" />
                        Hide author name
                      </label>
                      <label className="flex items-center gap-2 text-sm" data-testid="blog-hide-date-toggle">
                        <input type="checkbox" checked={blogForm.hide_date} onChange={(e) => setBlogForm({...blogForm, hide_date: e.target.checked})} className="w-4 h-4 accent-[#FF2D87]" />
                        Hide publish date
                      </label>
                      <label className="flex items-center gap-2 text-sm" data-testid="blog-post-as-brand-toggle">
                        <input type="checkbox" checked={blogForm.post_as_brand} onChange={(e) => setBlogForm({...blogForm, post_as_brand: e.target.checked})} className="w-4 h-4 accent-[#FF2D87]" />
                        Post as The Creative Ensemble
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={blogForm.is_published} onChange={(e) => setBlogForm({...blogForm, is_published: e.target.checked})} className="w-4 h-4 accent-[#FF2D87]" />
                        Published (visible on site)
                      </label>
                    </div>

                    <button type="submit" className="btn-primary w-full" data-testid="blog-submit-btn">
                      {editingPostId ? "Save changes" : "Create post"}
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-3">
              {posts.map((post) => (
                <div key={post.post_id} className="bg-white border-2 border-[#E5E5E5] p-4 flex justify-between items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{post.title}</p>
                    <p className="text-sm text-[#4A4A4A] truncate">By {post.author_name} — {new Date(post.created_at).toLocaleDateString()}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {post.content_type && post.content_type !== "update" && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#5170FF] bg-[#5170FF] text-white">
                          {(CONTENT_TYPE_OPTIONS.find((o) => o.value === post.content_type) || {}).label || post.content_type}
                        </span>
                      )}
                      {post.visibility === "portal_only" && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#0A0A0A] bg-[#0A0A0A] text-white">Portal only</span>
                      )}
                      {post.visibility === "both" && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#FF2D87] bg-[#FF2D87] text-white">Public + Portal</span>
                      )}
                      {post.hide_author && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#0A0A0A]">Author hidden</span>}
                      {post.hide_date && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#0A0A0A]">Date hidden</span>}
                      {!post.is_published && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#0A0A0A] bg-[#FFC2E8]">Draft</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEditBlog(post)} className="text-[#0A0A0A] hover:text-[#FF2D87]" data-testid={`edit-blog-${post.post_id}`} title="Edit">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteBlogPost(post.post_id)} className="text-[#FF2D87] hover:text-[#0A0A0A]" data-testid={`delete-blog-${post.post_id}`} title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {posts.length === 0 && <p className="text-center text-[#4A4A4A] py-12">No blog posts yet</p>}
            </div>
          </TabsContent>

          {/* Discussions Tab */}
          <TabsContent value="discussions">
            <div className="space-y-3">
              {discussions.map((d) => (
                <div key={d.discussion_id} className="bg-white border-2 border-[#E5E5E5] p-4 flex justify-between items-center">
                  <div>
                    <p className="font-bold">{d.title}</p>
                    <p className="text-sm text-[#4A4A4A]">{d.author_name} — {d.replies_count} replies</p>
                  </div>
                  <button onClick={() => deleteDiscussion(d.discussion_id)} className="text-[#FF2D87] hover:text-[#0A0A0A]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {discussions.length === 0 && <p className="text-center text-[#4A4A4A] py-12">No discussions yet</p>}
            </div>
          </TabsContent>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers">
            <div className="space-y-3">
              {subscribers.map((sub) => (
                <div key={sub.subscriber_id} className="bg-white border-2 border-[#E5E5E5] p-4">
                  <p className="font-bold text-sm">{sub.email}</p>
                  <p className="text-xs text-[#4A4A4A]">{sub.name || "—"} • Subscribed {new Date(sub.created_at).toLocaleDateString()}</p>
                </div>
              ))}
              {subscribers.length === 0 && <p className="text-center text-[#4A4A4A] py-12">No subscribers yet</p>}
            </div>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <div className="mb-6">
              <Dialog open={isTestimonialDialogOpen} onOpenChange={setIsTestimonialDialogOpen}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="add-testimonial-btn">
                    <Plus className="w-4 h-4" /> Add Testimonial
                  </button>
                </DialogTrigger>
                <DialogContent className="dialog-content sm:max-w-lg">
                  <DialogHeader><DialogTitle className="text-2xl font-display">Add Testimonial</DialogTitle></DialogHeader>
                  <form onSubmit={createTestimonial} className="space-y-4 mt-4">
                    <input type="text" value={testimonialForm.name} onChange={(e) => setTestimonialForm({...testimonialForm, name: e.target.value})} placeholder="Name" className="input-underline" required />
                    <input type="text" value={testimonialForm.title} onChange={(e) => setTestimonialForm({...testimonialForm, title: e.target.value})} placeholder="Title (e.g., Brand Strategist)" className="input-underline" required />
                    <input type="text" value={testimonialForm.company} onChange={(e) => setTestimonialForm({...testimonialForm, company: e.target.value})} placeholder="Company (optional)" className="input-underline" />
                    <textarea value={testimonialForm.content} onChange={(e) => setTestimonialForm({...testimonialForm, content: e.target.value})} placeholder="Their testimonial..." className="form-textarea" rows={4} required />
                    <button type="submit" className="btn-primary w-full">Add Testimonial</button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <p className="text-[#4A4A4A] text-sm">Testimonials will appear on the landing page once added.</p>
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <div className="mb-6">
              <Dialog open={isPartnerDialogOpen} onOpenChange={setIsPartnerDialogOpen}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="add-partner-btn">
                    <Plus className="w-4 h-4" /> Add Partner
                  </button>
                </DialogTrigger>
                <DialogContent className="dialog-content sm:max-w-lg">
                  <DialogHeader><DialogTitle className="text-2xl font-display">Add Partner</DialogTitle></DialogHeader>
                  <form onSubmit={createPartner} className="space-y-4 mt-4">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] mb-2 block">Brand Logo (PNG/SVG with transparent background works best)</label>
                      <div className="flex items-center gap-4">
                        <input type="file" accept="image/*" onChange={handlePartnerLogoUpload} className="text-sm" data-testid="partner-logo-input" disabled={partnerLogoUploading} />
                        {partnerLogoUploading && <span className="text-xs text-[#4A4A4A]">Uploading…</span>}
                      </div>
                      {partnerForm.logo_url && (
                        <div className="mt-3 border-2 border-[#0A0A0A] p-4 bg-white inline-block">
                          <img src={partnerForm.logo_url} alt="Logo preview" className="h-16 max-w-[200px] object-contain" />
                        </div>
                      )}
                    </div>
                    <input type="text" value={partnerForm.name} onChange={(e) => setPartnerForm({...partnerForm, name: e.target.value})} placeholder="Brand Name" className="input-underline" required data-testid="partner-name-input" />
                    <input type="url" value={partnerForm.website} onChange={(e) => setPartnerForm({...partnerForm, website: e.target.value})} placeholder="Website URL (optional)" className="input-underline" data-testid="partner-website-input" />
                    <input type="number" value={partnerForm.display_order} onChange={(e) => setPartnerForm({...partnerForm, display_order: parseInt(e.target.value, 10) || 0})} placeholder="Display order (lower = first)" className="input-underline" data-testid="partner-order-input" />
                    <button type="submit" className="btn-primary w-full" data-testid="save-partner-btn">Add Partner</button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {partners.length === 0 ? (
              <p className="text-[#4A4A4A] text-sm">No partners yet. The "Our Partners" section on the landing page is hidden until you add one.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {partners.map((p) => (
                  <div key={p.partner_id} className="bg-white border-2 border-[#0A0A0A] shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] p-5 flex items-start gap-4" data-testid={`admin-partner-${p.partner_id}`}>
                    <div className="w-20 h-20 border border-[#E5E5E5] flex items-center justify-center bg-white flex-shrink-0">
                      <img src={p.logo_url} alt={p.name} className="max-w-full max-h-full object-contain" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold truncate">{p.name}</p>
                      {p.website && (
                        <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-xs text-[#5170FF] hover:underline truncate block">
                          {p.website}
                        </a>
                      )}
                      <p className="text-[10px] uppercase tracking-wider text-[#4A4A4A] mt-1">Order: {p.display_order || 0}</p>
                    </div>
                    <button onClick={() => deletePartner(p.partner_id)} className="text-[#FF2D87] hover:text-red-600 transition-colors" data-testid={`delete-partner-${p.partner_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Reminders Tab */}
          <TabsContent value="reminders">
            <div className="bg-white border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] p-6 mb-6">
              <h3 className="text-lg font-display font-bold mb-2">Profile Completion Reminders</h3>
              <p className="text-sm text-[#4A4A4A] mb-4">
                Send an email to members whose profile is missing a photo or bio.
                Members with incomplete profiles do not appear in the directory.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={loadIncomplete}
                  disabled={remindersLoading}
                  className="px-5 py-2.5 border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-xs font-bold uppercase tracking-widest rounded-full transition-colors disabled:opacity-50"
                  data-testid="reload-incomplete-btn"
                >
                  {remindersLoading ? "Loading…" : "Load Incomplete Members"}
                </button>
                {incompleteMembers.length > 0 && (
                  <>
                    <button
                      onClick={() => sendReminders("friendly")}
                      disabled={reminderSending || selectedReminderIds.length === 0}
                      className="px-5 py-2.5 bg-[#FFC2E8] text-[#0A0A0A] hover:bg-[#FF2D87] hover:text-white text-xs font-bold uppercase tracking-widest rounded-full transition-colors disabled:opacity-50"
                      data-testid="send-friendly-btn"
                    >
                      {reminderSending ? "Sending…" : `Send Friendly Nudge (${selectedReminderIds.length})`}
                    </button>
                    <button
                      onClick={() => sendReminders("firm")}
                      disabled={reminderSending || selectedReminderIds.length === 0}
                      className="px-5 py-2.5 bg-[#FF2D87] text-white hover:bg-[#0A0A0A] text-xs font-bold uppercase tracking-widest rounded-full transition-colors disabled:opacity-50"
                      data-testid="send-firm-btn"
                    >
                      {reminderSending ? "Sending…" : `Send Final Notice (${selectedReminderIds.length})`}
                    </button>
                  </>
                )}
              </div>
            </div>

            {incompleteMembers.length === 0 ? (
              <p className="text-[#4A4A4A] text-sm">
                Click "Load Incomplete Members" above to view who's missing a photo or bio.
              </p>
            ) : (
              <div className="bg-white border-2 border-[#0A0A0A]">
                <table className="w-full text-sm">
                  <thead className="bg-[#FFC2E8] border-b-2 border-[#0A0A0A]">
                    <tr>
                      <th className="text-left p-3 w-10">
                        <input
                          type="checkbox"
                          checked={incompleteMembers.length > 0 && selectedReminderIds.length === incompleteMembers.length}
                          onChange={toggleSelectAllReminders}
                          className="w-4 h-4 accent-[#FF2D87] cursor-pointer"
                          data-testid="reminder-select-all"
                          aria-label="Select all"
                        />
                      </th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider font-bold">Name</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider font-bold">Email</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider font-bold">Missing</th>
                      <th className="text-left p-3 text-xs uppercase tracking-wider font-bold">Last Reminder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incompleteMembers.map((m) => (
                      <tr key={m.user_id} className="border-b border-[#E5E5E5]" data-testid={`incomplete-row-${m.user_id}`}>
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedReminderIds.includes(m.user_id)}
                            onChange={() => toggleReminderSelection(m.user_id)}
                            className="w-4 h-4 accent-[#FF2D87] cursor-pointer"
                            data-testid={`reminder-select-${m.user_id}`}
                            aria-label={`Select ${m.name || m.email}`}
                          />
                        </td>
                        <td className="p-3">{m.name || "—"}</td>
                        <td className="p-3 text-[#4A4A4A]">{m.email || "—"}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {m.missing_photo && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FF2D87] text-white px-2 py-0.5 rounded-full">Photo</span>}
                            {m.missing_bio && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FF2D87] text-white px-2 py-0.5 rounded-full">Bio</span>}
                            {m.missing_title && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FF2D87] text-white px-2 py-0.5 rounded-full">Title</span>}
                            {m.missing_location && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FF2D87] text-white px-2 py-0.5 rounded-full">Location</span>}
                            {m.missing_skills && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FF2D87] text-white px-2 py-0.5 rounded-full">Skills</span>}
                            {m.missing_goals && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FF2D87] text-white px-2 py-0.5 rounded-full">Goals</span>}
                          </div>
                        </td>
                        <td className="p-3 text-[#4A4A4A] text-xs">
                          {m.last_reminder_sent
                            ? `${new Date(m.last_reminder_sent).toLocaleDateString()} (${m.last_reminder_tone || "—"})`
                            : "Never"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-display font-bold mb-1">Contact form messages</h2>
                <p className="text-sm text-[#4A4A4A]">Submissions from the public Contact page.</p>
              </div>
              <button onClick={loadContactMessages} className="text-xs font-bold uppercase tracking-widest text-[#FF2D87] hover:text-[#0A0A0A]" data-testid="refresh-messages">
                Refresh
              </button>
            </div>
            {messagesLoading ? (
              <p className="text-center text-[#4A4A4A] py-8">Loading…</p>
            ) : contactMessages.length === 0 ? (
              <div className="bg-white border-2 border-[#E5E5E5] p-10 text-center">
                <p className="text-[#4A4A4A]">No messages yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contactMessages.map((m) => (
                  <div
                    key={m.message_id}
                    className={`bg-white border-2 ${m.is_read ? "border-[#E5E5E5]" : "border-[#FF2D87]"} p-5`}
                    data-testid={`message-${m.message_id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="font-bold text-[#0A0A0A] flex items-center gap-2">
                          {m.name}
                          {!m.is_read && <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FF2D87] text-white px-2 py-0.5 rounded-full">New</span>}
                        </p>
                        <p className="text-xs text-[#4A4A4A]">
                          <a href={`mailto:${m.email}`} className="text-[#5170FF] hover:underline">{m.email}</a>
                          {" • "}
                          {new Date(m.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!m.is_read && (
                          <button
                            onClick={() => markMessageRead(m.message_id)}
                            className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A] hover:text-[#FF2D87]"
                            data-testid={`mark-read-${m.message_id}`}
                          >
                            Mark read
                          </button>
                        )}
                        <a
                          href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "Your message to The Creative Ensemble")}`}
                          className="text-xs font-bold uppercase tracking-widest text-[#FF2D87] hover:text-[#0A0A0A]"
                        >
                          Reply
                        </a>
                        <button onClick={() => deleteContactMessage(m.message_id)} className="text-[#FF2D87] hover:text-[#0A0A0A]" data-testid={`delete-message-${m.message_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {m.subject && <p className="text-sm font-semibold text-[#0A0A0A] mb-1">{m.subject}</p>}
                    <p className="text-sm text-[#0A0A0A]/80 whitespace-pre-wrap">{m.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Suggestions Tab */}
          <TabsContent value="suggestions">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-display font-bold mb-1">Member suggestions</h2>
                <p className="text-sm text-[#4A4A4A]">Private suggestions submitted from My Portal.</p>
              </div>
              <button onClick={loadSuggestions} className="text-xs font-bold uppercase tracking-widest text-[#FF2D87] hover:text-[#0A0A0A]" data-testid="refresh-suggestions">
                Refresh
              </button>
            </div>
            {suggestionsLoading ? (
              <p className="text-center text-[#4A4A4A] py-8">Loading…</p>
            ) : suggestions.length === 0 ? (
              <div className="bg-white border-2 border-[#E5E5E5] p-10 text-center">
                <p className="text-[#4A4A4A]">No suggestions yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {suggestions.map((s) => (
                  <div
                    key={s.suggestion_id}
                    className={`bg-white border-2 ${s.is_read ? "border-[#E5E5E5]" : "border-[#5170FF]"} p-5`}
                    data-testid={`suggestion-${s.suggestion_id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="font-bold text-[#0A0A0A] flex items-center gap-2">
                          {s.user_name}
                          {!s.is_read && <span className="text-[10px] font-bold uppercase tracking-widest bg-[#5170FF] text-white px-2 py-0.5 rounded-full">New</span>}
                          {s.category && s.category !== "other" && (
                            <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FFC2E8] text-[#0A0A0A] px-2 py-0.5 rounded-full">{s.category}</span>
                          )}
                        </p>
                        <p className="text-xs text-[#4A4A4A]">
                          <a href={`mailto:${s.user_email}`} className="text-[#5170FF] hover:underline">{s.user_email}</a>
                          {" • "}
                          {new Date(s.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!s.is_read && (
                          <button
                            onClick={() => markSuggestionRead(s.suggestion_id)}
                            className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A] hover:text-[#FF2D87]"
                            data-testid={`mark-read-suggestion-${s.suggestion_id}`}
                          >
                            Mark read
                          </button>
                        )}
                        <a
                          href={`mailto:${s.user_email}?subject=${encodeURIComponent("Re: your suggestion to The Creative Ensemble")}`}
                          className="text-xs font-bold uppercase tracking-widest text-[#FF2D87] hover:text-[#0A0A0A]"
                        >
                          Reply
                        </a>
                        <button onClick={() => deleteSuggestion(s.suggestion_id)} className="text-[#FF2D87] hover:text-[#0A0A0A]" data-testid={`delete-suggestion-${s.suggestion_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-[#0A0A0A]/80 whitespace-pre-wrap">{s.content}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Event Pitches Tab */}
          <TabsContent value="pitches">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-display font-bold mb-1">Event pitches</h2>
                <p className="text-sm text-[#4A4A4A]">Workshop & meetup pitches submitted from the Events page.</p>
              </div>
              <button onClick={loadPitches} className="text-xs font-bold uppercase tracking-widest text-[#FF2D87] hover:text-[#0A0A0A]" data-testid="refresh-pitches">
                Refresh
              </button>
            </div>
            {pitchesLoading ? (
              <p className="text-center text-[#4A4A4A] py-8">Loading…</p>
            ) : pitches.length === 0 ? (
              <div className="bg-white border-2 border-[#E5E5E5] p-10 text-center">
                <p className="text-[#4A4A4A]">No event pitches yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pitches.map((p) => (
                  <div
                    key={p.pitch_id}
                    className={`bg-white border-2 ${p.is_read ? "border-[#E5E5E5]" : "border-[#5170FF]"} p-5`}
                    data-testid={`pitch-${p.pitch_id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="font-display text-lg font-bold text-[#0A0A0A] flex items-center gap-2 flex-wrap">
                          {p.title}
                          {!p.is_read && <span className="text-[10px] font-bold uppercase tracking-widest bg-[#5170FF] text-white px-2 py-0.5 rounded-full">New</span>}
                          {p.co_host && <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FF2D87] text-white px-2 py-0.5 rounded-full">Wants to co-host</span>}
                          {p.location_type && <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FFC2E8] text-[#0A0A0A] px-2 py-0.5 rounded-full">{p.location_type}</span>}
                        </p>
                        <p className="text-xs text-[#4A4A4A] mt-1">
                          By <strong>{p.user_name}</strong> · <a href={`mailto:${p.user_email}`} className="text-[#5170FF] hover:underline">{p.user_email}</a>
                          {" • "}
                          {new Date(p.created_at).toLocaleString()}
                          {p.proposed_date && <> · proposed: {p.proposed_date}</>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!p.is_read && (
                          <button
                            onClick={() => markPitchRead(p.pitch_id)}
                            className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A] hover:text-[#FF2D87]"
                            data-testid={`mark-read-pitch-${p.pitch_id}`}
                          >
                            Mark read
                          </button>
                        )}
                        <a
                          href={`mailto:${p.user_email}?subject=${encodeURIComponent(`Re: ${p.title}`)}`}
                          className="text-xs font-bold uppercase tracking-widest text-[#FF2D87] hover:text-[#0A0A0A]"
                        >
                          Reply
                        </a>
                        <button onClick={() => deletePitch(p.pitch_id)} className="text-[#FF2D87] hover:text-[#0A0A0A]" data-testid={`delete-pitch-${p.pitch_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {p.audience && (
                      <p className="text-xs text-[#4A4A4A] mb-2"><strong>Audience:</strong> {p.audience}</p>
                    )}
                    <p className="text-sm text-[#0A0A0A]/80 whitespace-pre-wrap">{p.description}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
